import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, ChevronLeft, ChevronRight, CalendarDays, CalendarX, Clock, User, DoorOpen, Plus, X, CheckCircle2, Repeat, Lock, AlertTriangle, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { paymentSummary, PAYMENT_TONE } from "@/lib/payment";
import AppointmentRefundModal from "@/pages/pos/AppointmentRefundModal";
import { fmtTimeRange } from "@/lib/format";

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function mondayOf(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // 0 = Monday
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
}
const iso = (d) => d.toISOString().slice(0, 10);
const fmtDay = (s) => { const d = new Date(s + "T00:00:00"); return { dow: DOW[d.getDay()], num: d.getDate(), month: d.toLocaleString("default", { month: "short" }) }; };

export default function ManagerCalendar() {
  const qc = useQueryClient();
  const [start, setStart] = useState(() => iso(mondayOf(new Date())));
  const [bookDate, setBookDate] = useState(null);
  const [manage, setManage] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ["mgr-schedule", start],
    queryFn: () => api.get(`/manager/me/schedule?start=${start}&days=7`).then((r) => r.data),
  });

  const shift = (weeks) => {
    const d = new Date(start + "T00:00:00");
    d.setDate(d.getDate() + weeks * 7);
    setStart(iso(d));
  };

  const days = data?.days || [];
  const roster = data?.roster || [];
  const todayIso = iso(new Date());
  const rangeLabel = days.length ? `${fmtDay(days[0].date).month} ${fmtDay(days[0].date).num} – ${fmtDay(days[6].date).month} ${fmtDay(days[6].date).num}` : "";

  return (
    <div data-testid="mgr-calendar-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2"><CalendarDays className="text-[#144F75]" /> Booking calendar</h2>
        <div className="flex items-center gap-2">
          <button onClick={() => shift(-1)} data-testid="cal-prev" className="rounded-full border border-[#E5E0D8] p-2 hover:border-[#144F75] text-[#4A5D6B]"><ChevronLeft size={16} /></button>
          <span className="text-sm font-semibold text-[#1A242B] min-w-[150px] text-center">{rangeLabel}</span>
          <button onClick={() => shift(1)} data-testid="cal-next" className="rounded-full border border-[#E5E0D8] p-2 hover:border-[#144F75] text-[#4A5D6B]"><ChevronRight size={16} /></button>
          <input type="date" value={start} onChange={(e) => { if (e.target.value) setStart(iso(mondayOf(new Date(e.target.value + "T00:00:00")))); }} data-testid="cal-jump-date" className="rounded-full border border-[#E5E0D8] px-3 py-1.5 text-xs text-[#4A5D6B] focus:border-[#144F75] outline-none ml-1" title="Jump to week" />
          <button onClick={() => setStart(iso(mondayOf(new Date())))} data-testid="cal-today" className="eyw-btn-ghost !py-2 text-xs">This week</button>
        </div>
      </div>
      <p className="text-sm text-[#4A5D6B] mb-4">Appointments booked at your center this week. Open time in a practitioner's availability = a bookable slot.</p>

      {isLoading ? <Loader2 className="animate-spin text-[#144F75]" /> : (
        <div className="grid grid-cols-1 md:grid-cols-7 gap-2">
          {days.map((d) => {
            const info = fmtDay(d.date);
            const isToday = d.date === todayIso;
            return (
              <div key={d.date} data-testid={`cal-day-${d.date}`} className={`rounded-2xl border p-2.5 min-h-[140px] ${isToday ? "border-[#144F75] bg-[#144F75]/[0.03]" : "border-[#E5E0D8] bg-white"}`}>
                <div className="text-center mb-2">
                  <p className="text-[11px] uppercase tracking-wide text-[#8FA89B]">{info.dow}</p>
                  <p className={`font-serif text-lg ${isToday ? "text-[#144F75]" : "text-[#1A242B]"}`}>{info.num}</p>
                </div>
                <div className="space-y-1.5">
                  {(d.blackouts || []).map((bl, i) => (
                    <div key={`bl${i}`} data-testid={`cal-blackout-${d.date}-${i}`} className="rounded-lg bg-red-50 border border-red-200 px-2 py-1">
                      <p className="text-[10px] font-semibold text-red-600 flex items-center gap-1"><CalendarX size={9} /> {bl.scope === "center" ? "Center blocked" : `${bl.enhancer_name || "Enhancer"} blocked`}{bl.start_time ? ` · ${fmtTimeRange(bl.start_time, bl.end_time)}` : ""}</p>
                      {bl.reason && <p className="text-[9px] text-red-400 truncate">{bl.reason}</p>}
                    </div>
                  ))}
                  {d.appointments.length === 0 ? (
                    <p className="text-[11px] text-[#B5B0A6] text-center py-2">No bookings</p>
                  ) : d.appointments.map((a) => (
                    <button key={a.id} onClick={() => setManage({ ...a, date: d.date })} data-testid={`cal-appt-${a.id}`} className="w-full text-left rounded-lg bg-[#F9F7F3] border border-[#E5E0D8] px-2 py-1.5 hover:border-[#144F75] transition-colors">
                      <p className="text-[11px] font-semibold text-[#144F75] flex items-center gap-1"><Clock size={10} /> {fmtTimeRange(a.start_time)} · {a.duration_min}m {a.recurrence_id && <Repeat size={9} className="text-[#B5794E]" />}</p>
                      <p className="text-[12px] text-[#1A242B] leading-tight truncate">{a.service_title}</p>
                      <p className="text-[10px] text-[#4A5D6B] truncate flex items-center gap-1"><User size={9} /> {a.client}</p>
                      {a.room_name && <p className="text-[10px] text-[#8FA89B] truncate flex items-center gap-1"><DoorOpen size={9} /> {a.room_name}</p>}
                      {(() => { const p = paymentSummary(a); return <span className={`inline-block mt-1 text-[9px] px-1.5 py-0.5 rounded-full ${PAYMENT_TONE[p.tone]}`} data-testid={`cal-payment-${a.id}`}>{p.label}</span>; })()}
                    </button>
                  ))}
                  <button onClick={() => setBookDate(d.date)} data-testid={`cal-add-${d.date}`}
                    className="w-full mt-1 rounded-lg border border-dashed border-[#144F75]/40 text-[#144F75] text-[11px] py-1.5 hover:bg-[#144F75]/[0.05] flex items-center justify-center gap-1 transition-colors"><Plus size={12} /> Book</button>
                  {(d.auto_blocks || []).map((ab, i) => (
                    <div key={i} data-testid={`cal-autoblock-${d.date}-${i}`} className="rounded-lg bg-[#F1EDE6] border border-dashed border-[#C9B79E] px-2 py-1">
                      <p className="text-[10px] font-semibold text-[#8A6D3B] flex items-center gap-1"><Lock size={9} /> {ab.start}–{ab.end} · {ab.room_name}</p>
                      <p className="text-[9px] text-[#A38A5C] truncate">Auto-blocked · {ab.reason}</p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {roster.length > 0 && (
        <div className="eyw-card p-4 mt-5">
          <p className="text-sm font-semibold text-[#1A242B] mb-2">Practitioner availability (weekly)</p>
          <div className="space-y-1.5">
            {roster.map((r) => (
              <div key={r.enhancer_id} className="text-xs text-[#4A5D6B] flex gap-2 flex-wrap" data-testid={`cal-roster-${r.enhancer_id}`}>
                <span className="font-semibold text-[#1A242B] min-w-[110px]">{r.name}</span>
                {r.availability.length === 0 ? <span className="text-[#B5794E]">No availability set (bookable when a room is free)</span>
                  : r.availability.map((s, i) => <span key={i} className="bg-[#144F75]/[0.06] rounded-full px-2 py-0.5">{DOW[s.day]} {s.start}–{s.end}</span>)}
              </div>
            ))}
          </div>
        </div>
      )}
      {bookDate && (
        <NewBookingModal date={bookDate} onClose={() => setBookDate(null)}
          onBooked={() => { setBookDate(null); qc.invalidateQueries({ queryKey: ["mgr-schedule"] }); }} />
      )}
      {manage && (
        <ManageBookingModal appt={manage} onClose={() => setManage(null)}
          onDone={() => { setManage(null); qc.invalidateQueries({ queryKey: ["mgr-schedule"] }); }} />
      )}
    </div>
  );
}

function ManageBookingModal({ appt, onClose, onDone }) {
  const isSeries = !!appt.recurrence_id;
  const [mode, setMode] = useState(null); // 'reschedule' | 'cancel'
  const [showRefund, setShowRefund] = useState(false);
  const [scope, setScope] = useState("one");
  const [date, setDate] = useState(appt.date);
  const [time, setTime] = useState(appt.start_time);
  const [busy, setBusy] = useState(false);

  const doReschedule = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/manager/me/bookings/${appt.id}/reschedule`, { date, start_time: time, scope });
      toast.success(data.series ? `Rescheduled ${data.updated} occurrence(s)${data.skipped?.length ? ` · ${data.skipped.length} skipped` : ""}` : "Booking rescheduled");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not reschedule."); }
    finally { setBusy(false); }
  };
  const doCancel = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/manager/me/bookings/${appt.id}/cancel`, { scope });
      toast.success(`Cancelled ${data.cancelled} booking${data.cancelled === 1 ? "" : "s"}`);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not cancel."); }
    finally { setBusy(false); }
  };

  const ScopeChoice = () => isSeries ? (
    <div className="rounded-xl bg-[#F9F7F3] border border-[#E5E0D8] p-3 mb-4" data-testid="manage-scope">
      <p className="text-xs text-[#4A5D6B] mb-2 flex items-center gap-1.5"><Repeat size={13} className="text-[#B5794E]" /> This is a recurring booking. Apply to:</p>
      <div className="flex gap-2">
        <button onClick={() => setScope("one")} data-testid="manage-scope-one" className={`flex-1 rounded-full py-2 text-xs border ${scope === "one" ? "bg-[#144F75] text-white border-[#144F75]" : "border-[#E5E0D8] text-[#4A5D6B]"}`}>Just this one</button>
        <button onClick={() => setScope("future")} data-testid="manage-scope-future" className={`flex-1 rounded-full py-2 text-xs border ${scope === "future" ? "bg-[#144F75] text-white border-[#144F75]" : "border-[#E5E0D8] text-[#4A5D6B]"}`}>This &amp; all future</button>
      </div>
    </div>
  ) : null;

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center px-4 bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-7" data-testid="manage-booking-modal">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-serif text-2xl text-[#1A242B]">Manage booking</h3>
          <button onClick={onClose} data-testid="manage-close" className="text-[#4A5D6B] hover:text-[#144F75]"><X size={20} /></button>
        </div>
        <p className="text-sm text-[#1A242B] font-medium">{appt.service_title}</p>
        <p className="text-sm text-[#4A5D6B] mb-5">{appt.client} · {fmtTimeRange(appt.start_time)}{appt.enhancer_name ? ` · ${appt.enhancer_name}` : ""}{isSeries ? " · recurring" : ""}</p>

        {!mode && (
          <>
            <div className="flex gap-3">
              <button onClick={() => setMode("reschedule")} data-testid="manage-reschedule-btn" className="eyw-btn flex-1 justify-center text-sm flex items-center gap-1.5"><Clock size={15} /> Reschedule</button>
              <button onClick={() => setMode("cancel")} data-testid="manage-cancel-btn" className="flex-1 justify-center rounded-full border border-red-200 text-red-600 hover:bg-red-50 py-2.5 text-sm flex items-center gap-1.5"><X size={15} /> Cancel</button>
            </div>
            {appt.status !== "refunded" && appt.payment_status !== "refunded" && (
              <button onClick={() => setShowRefund(true)} data-testid="manage-refund-btn" className="w-full mt-3 justify-center rounded-full border border-[#E5E0D8] text-[#B45151] hover:bg-[#B45151]/5 py-2.5 text-sm flex items-center gap-1.5">
                <RotateCcw size={15} /> {appt.payment_status === "package" ? "Restore credit" : "Refund"}
              </button>
            )}
          </>
        )}
        {showRefund && <AppointmentRefundModal bookingId={appt.id} onClose={() => setShowRefund(false)} onDone={() => { onDone?.(); onClose(); }} />}

        {mode === "reschedule" && (
          <div data-testid="manage-reschedule-form">
            <ScopeChoice />
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div><label className="text-sm font-medium text-[#1A242B] mb-1 block">Date</label>
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="manage-date" className="eyw-input w-full" /></div>
              <div><label className="text-sm font-medium text-[#1A242B] mb-1 block">Time</label>
                <input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="manage-time" className="eyw-input w-full" /></div>
            </div>
            {scope === "future" && <p className="text-[11px] text-[#8FA89B] mb-3">Future occurrences shift by the same amount and move to the new time.</p>}
            <button onClick={doReschedule} disabled={busy} data-testid="manage-reschedule-save" className="eyw-btn w-full justify-center disabled:opacity-60 flex items-center gap-1.5">{busy ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle2 size={16} />} Save new time</button>
          </div>
        )}

        {mode === "cancel" && (
          <div data-testid="manage-cancel-form">
            <ScopeChoice />
            <p className="text-sm text-[#4A5D6B] mb-4">{isSeries && scope === "future" ? "This will cancel this and all upcoming occurrences in the series." : "This will cancel this appointment."}</p>
            <button onClick={doCancel} disabled={busy} data-testid="manage-cancel-confirm" className="w-full justify-center rounded-full bg-red-600 hover:bg-red-700 text-white py-2.5 text-sm disabled:opacity-60 flex items-center gap-1.5">{busy ? <Loader2 className="animate-spin" size={16} /> : <X size={16} />} Confirm cancellation</button>
          </div>
        )}
      </div>
    </div>
  );
}

export function NewBookingModal({ date, onClose, onBooked, initialTime }) {
  const [serviceId, setServiceId] = useState("");
  const [enhancerId, setEnhancerId] = useState("");
  const [time, setTime] = useState(initialTime || "10:00");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientQuery, setClientQuery] = useState("");
  const [showClients, setShowClients] = useState(false);
  const [notes, setNotes] = useState("");
  const [repeatWeeks, setRepeatWeeks] = useState(1);
  const [override, setOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [saving, setSaving] = useState(false);

  const { data } = useQuery({
    queryKey: ["mgr-service-practitioners"],
    queryFn: () => api.get("/manager/me/service-practitioners").then((r) => r.data),
  });
  const services = data?.services || [];
  const roster = data?.roster || [];
  const rosterById = Object.fromEntries(roster.map((r) => [r.enhancer_id, r]));
  const svc = services.find((s) => s.id === serviceId);
  const practitioners = svc ? (svc.practitioner_ids || []) : [];

  const { data: clientResults = [] } = useQuery({
    queryKey: ["mgr-clients", clientQuery],
    queryFn: () => api.get(`/manager/me/clients?q=${encodeURIComponent(clientQuery)}`).then((r) => r.data),
    enabled: showClients && clientQuery.length >= 2,
  });

  const { data: slotData } = useQuery({
    queryKey: ["mgr-open-slots", serviceId, enhancerId, date],
    queryFn: () => api.get(`/manager/me/open-slots?service_id=${serviceId}&date=${date}&enhancer_id=${enhancerId}`).then((r) => r.data),
    enabled: !!serviceId,
  });
  const slots = slotData?.slots || [];

  // Live room-busy warning (same engine as the events modal, includes linked-room auto-blocks).
  const roomKey = svc?.room || "";
  const { data: roomAvail } = useQuery({
    queryKey: ["mgr-book-avail", roomKey, date],
    queryFn: () => api.get(`/venue/availability?room_key=${roomKey}&date=${date}`).then((r) => r.data),
    enabled: !!roomKey && !!date,
  });
  const bToMin = (t) => { const [h, m] = String(t).split(":"); return (parseInt(h) || 0) * 60 + (parseInt(m) || 0); };
  const bDur = slotData?.duration || svc?.duration_min || 60;
  const bClashes = (roomAvail?.busy || []).filter((b) => bToMin(time) < bToMin(b.end) && bToMin(b.start) < bToMin(time) + bDur);

  const pickClient = (c) => {
    setClientId(c.id); setName(c.name); setEmail(c.email);
    setClientQuery(c.name); setShowClients(false);
  };

  const submit = async () => {
    if (!serviceId) { toast.error("Pick a service."); return; }
    if (!name && !email && !clientId) { toast.error("Add or pick a client."); return; }
    if (bClashes.length && !override) { toast.error("This time conflicts with another booking. Tick “Book anyway” with a reason to override."); return; }
    if (bClashes.length && override && !overrideReason.trim()) { toast.error("Add a reason for overriding the conflict."); return; }
    setSaving(true);
    try {
      const { data: res } = await api.post("/manager/me/bookings", { service_id: serviceId, date, start_time: time, name, email, client_id: clientId, enhancer_id: enhancerId, notes, repeat_weeks: repeatWeeks, override, override_reason: overrideReason });
      if (res.recurring) {
        const skip = (res.skipped || []).length;
        toast.success(`Created ${res.created} weekly booking${res.created === 1 ? "" : "s"}${skip ? ` · ${skip} week${skip === 1 ? "" : "s"} skipped (conflicts)` : ""}`);
      } else {
        toast.success("Booking created");
      }
      onBooked();
    } catch (e) { const d = e.response?.data?.detail; toast.error((d && d.message) ? d.message : (formatApiError(d) || "Could not create booking.")); }
    finally { setSaving(false); }
  };

  const dt = new Date(date + "T00:00:00");
  return (
    <div className="fixed inset-0 z-[100] grid place-items-center px-4 bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-lg bg-white rounded-3xl shadow-2xl p-7 max-h-[90vh] overflow-y-auto" data-testid="cal-book-modal">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2"><CalendarDays size={22} className="text-[#144F75]" /> New booking</h3>
          <button onClick={onClose} data-testid="cal-book-close" className="text-[#4A5D6B] hover:text-[#144F75]"><X size={20} /></button>
        </div>
        <p className="text-sm text-[#4A5D6B] mb-5">{dt.toLocaleDateString("default", { weekday: "long", month: "long", day: "numeric" })}</p>

        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium text-[#1A242B] mb-1 block">Service</label>
            <select value={serviceId} onChange={(e) => { setServiceId(e.target.value); setEnhancerId(""); }} data-testid="cal-book-service" className="eyw-input w-full">
              <option value="">Choose a service…</option>
              {services.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
            </select>
          </div>
          {practitioners.length > 0 && (
            <div>
              <label className="text-sm font-medium text-[#1A242B] mb-1 block">Practitioner (optional)</label>
              <select value={enhancerId} onChange={(e) => setEnhancerId(e.target.value)} data-testid="cal-book-practitioner" className="eyw-input w-full">
                <option value="">Any / unassigned</option>
                {practitioners.map((id) => <option key={id} value={id}>{rosterById[id]?.name || "Practitioner"}</option>)}
              </select>
            </div>
          )}

          {serviceId && (
            <div>
              <label className="text-sm font-medium text-[#1A242B] mb-1 block">Open slots {slotData?.duration ? `(${slotData.duration} min)` : ""}</label>
              {slots.length === 0 ? (
                <p className="text-xs text-[#B5794E] mb-1" data-testid="cal-book-no-slots">No open slots that day — set the time manually below.</p>
              ) : (
                <div className="flex flex-wrap gap-1.5 mb-1" data-testid="cal-book-slots">
                  {slots.map((s) => (
                    <button key={s} onClick={() => setTime(s)} data-testid={`cal-book-slot-${s}`}
                      className={`rounded-full px-3 py-1.5 text-xs border transition-colors ${time === s ? "bg-[#144F75] text-white border-[#144F75]" : "border-[#E5E0D8] text-[#4A5D6B] hover:border-[#144F75]"}`}>{s}</button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div>
            <label className="text-sm font-medium text-[#1A242B] mb-1 block">Time</label>
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="cal-book-time" className="eyw-input w-full" />
          </div>

          <div className="relative">
            <label className="text-sm font-medium text-[#1A242B] mb-1 block">Find existing client</label>
            <input value={clientQuery} onChange={(e) => { setClientQuery(e.target.value); setShowClients(true); setClientId(""); }}
              placeholder="Search name, email or phone…" data-testid="cal-book-client-search" className="eyw-input w-full" />
            {showClients && clientQuery.length >= 2 && clientResults.length > 0 && (
              <div className="absolute z-10 left-0 right-0 mt-1 bg-white rounded-xl border border-[#E5E0D8] shadow-lg max-h-48 overflow-y-auto" data-testid="cal-book-client-results">
                {clientResults.map((c) => (
                  <button key={c.id} onClick={() => pickClient(c)} data-testid={`cal-book-client-${c.id}`} className="w-full text-left px-3 py-2 text-sm hover:bg-[#F9F7F3] flex flex-col">
                    <span className="text-[#1A242B]">{c.name}</span>
                    <span className="text-[11px] text-[#8FA89B]">{c.email}{c.phone ? ` · ${c.phone}` : ""}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-sm font-medium text-[#1A242B] mb-1 block">Client name</label>
              <input value={name} onChange={(e) => { setName(e.target.value); setClientId(""); }} placeholder="Jane Doe" data-testid="cal-book-name" className="eyw-input w-full" /></div>
            <div><label className="text-sm font-medium text-[#1A242B] mb-1 block">Email</label>
              <input value={email} onChange={(e) => { setEmail(e.target.value); setClientId(""); }} placeholder="jane@email.com" data-testid="cal-book-email" className="eyw-input w-full" /></div>
          </div>
          <div>
            <label className="text-sm font-medium text-[#1A242B] mb-1 block">Notes (optional)</label>
            <input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="cal-book-notes" className="eyw-input w-full" />
          </div>
          <div className="rounded-xl bg-[#F9F7F3] border border-[#E5E0D8] p-3 flex items-center gap-3 flex-wrap" data-testid="cal-book-repeat">
            <label className="flex items-center gap-2 text-sm text-[#2A3942] cursor-pointer">
              <input type="checkbox" checked={repeatWeeks > 1} onChange={(e) => setRepeatWeeks(e.target.checked ? 4 : 1)} className="accent-[#144F75]" data-testid="cal-book-repeat-toggle" />
              <span className="flex items-center gap-1.5"><Repeat size={14} className="text-[#144F75]" /> Repeat weekly</span>
            </label>
            {repeatWeeks > 1 && (
              <div className="flex items-center gap-2 text-sm text-[#4A5D6B]">
                for
                <select value={repeatWeeks} onChange={(e) => setRepeatWeeks(Number(e.target.value))} data-testid="cal-book-repeat-weeks" className="rounded-lg border border-[#E5E0D8] px-2 py-1.5 bg-white">
                  {[2, 3, 4, 6, 8, 12].map((n) => <option key={n} value={n}>{n} weeks</option>)}
                </select>
                <span className="text-xs text-[#8FA89B]">same day &amp; time</span>
              </div>
            )}
          </div>
        </div>

        {bClashes.length > 0 && (
          <div className="rounded-xl bg-[#FBEEE8] border border-[#E7C3B3] px-3 py-2.5 text-xs text-[#8A4B2E] mt-4" data-testid="cal-book-conflict-warning">
            <p className="font-semibold flex items-center gap-1.5"><AlertTriangle size={13} /> This room is busy at that time</p>
            {bClashes.map((b, i) => <p key={i} className="mt-0.5">{b.start}–{b.end} · {b.label}</p>)}
            <p className="mt-1 text-[#B5794E]">Pick an open slot above, choose another time, or override below.</p>
            <label className="flex items-center gap-2 mt-2 font-medium" data-testid="cal-book-override-label">
              <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} data-testid="cal-book-override" /> Book anyway (override conflict)
            </label>
            {override && (
              <input value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} data-testid="cal-book-override-reason"
                placeholder="Reason for overriding (required)" className="mt-2 w-full rounded-lg border border-[#E7C3B3] bg-white px-3 py-2 text-xs text-[#1A242B] outline-none focus:border-[#8A4B2E]" />
            )}
          </div>
        )}
        <button onClick={submit} disabled={saving} data-testid="cal-book-submit" className="eyw-btn w-full justify-center mt-5 disabled:opacity-60 flex items-center gap-1.5">
          {saving ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle2 size={16} />} Create booking
        </button>
      </div>
    </div>
  );
}
